import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
} from '@mui/material';
import { getReputation } from '../utils/contracts';

function Profile({ wallet, contracts }) {
  const [reputation, setReputation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userContent, setUserContent] = useState([]);

  useEffect(() => {
    if (wallet && contracts) {
      loadProfileData();
    }
  }, [wallet, contracts]);

  const loadProfileData = async () => {
    setLoading(true);
    try {
      const rep = await getReputation(contracts.reputationManager, wallet.address);
      setReputation(rep);

      // Mock data for demonstration
      setUserContent([
        {
          hash: 'ipfs://Qm...',
          type: 'image',
          timestamp: new Date().toISOString(),
          validations: 5,
          isAIGenerated: false,
        },
        // Add more mock content
      ]);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!wallet) {
    return <Alert severity="info">Please connect your wallet to view your profile.</Alert>;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Profile
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Reputation Score
              </Typography>
              <Box display="flex" alignItems="center" justifyContent="center" sx={{ p: 3 }}>
                <Typography variant="h3">{reputation?.toString() || '0'}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Your reputation score reflects your content creation and validation history
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Your Content History
              </Typography>
            </Box>
            <Divider />
            <List>
              {userContent.map((content, index) => (
                <React.Fragment key={content.hash}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box display="flex" justifyContent="space-between">
                          <Typography>Content Hash: {content.hash.slice(0, 10)}...</Typography>
                          <Typography color="text.secondary">
                            {new Date(content.timestamp).toLocaleDateString()}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography component="span" variant="body2">
                            Type: {content.type} • Validations: {content.validations} •
                            {content.isAIGenerated ? ' AI Generated' : ' Human Created'}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < userContent.length - 1 && <Divider />}
                </React.Fragment>
              ))}
              {userContent.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No content uploaded yet"
                    secondary="Start by uploading and registering your first content"
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Validation Activity
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your recent validation history and rewards will appear here
              </Typography>
              {/* Add validation history component here */}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Profile;
